import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getLoan, createPayment, formatCurrency } from '../api';
import { useConfig } from '../context/ConfigContext';
import LoanStatusNotices from '../components/LoanStatusNotices';
import { overdueBadgeFor } from '../utils/lateFeeDisplay';
// "Monthly Payment" is P&I plus the escrow frozen on this loan. loan.monthly_payment
// is P&I alone and is never shown under that label. See utils/monthlyPayment.js.
import { totalMonthlyPayment, monthlyPaymentParts, amountDue, arrearsBalance } from '../utils/monthlyPayment';

function LoanDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getCompanyLegalName, getCompanyPhone, getCompanyEmail, getSquareApplicationId, getSquareLocationId, getSquareEnvironment, hasCustomSquareCredentials } = useConfig();

  const [loan, setLoan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [payments, setPayments] = useState([]);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentBreakdown, setPaymentBreakdown] = useState(null);
  const [loadingBreakdown, setLoadingBreakdown] = useState(true);
  const [processing, setProcessing] = useState(false);
  // Synchronous double-submit guard. `processing` only disables the button on
  // the next render, so two clicks dispatched before React re-renders would
  // both get through. A ref flips immediately.
  const submitLockRef = useRef(false);
  const [paymentError, setPaymentError] = useState('');
  const [cardInstance, setCardInstance] = useState(null);
  const [selectedQuickAmount, setSelectedQuickAmount] = useState(null);
  // Extra principal the customer wants to add on top of the scheduled payment.
  // Held as a string so the field can be empty rather than showing a stray 0.
  const [extraPrincipal, setExtraPrincipal] = useState('');
  // A payment made when nothing is due: the whole amount goes to principal.
  const [principalOnly, setPrincipalOnly] = useState(false);
  const [quoting, setQuoting] = useState(false);
  // What the payment did, shown after it succeeds instead of navigating away.
  const [confirmation, setConfirmation] = useState(null);
  const [billingName, setBillingName] = useState('');
  const [billingAddress, setBillingAddress] = useState('');
  const [billingCity, setBillingCity] = useState('');
  const [billingState, setBillingState] = useState('');
  const [billingZip, setBillingZip] = useState('');
  const [squareLoaded, setSquareLoaded] = useState(false);

  // Dynamically load Square SDK based on tenant's environment
  useEffect(() => {
    // Only load if tenant has Square credentials configured
    if (!hasCustomSquareCredentials()) {
      return;
    }

    const loadSquareSDK = () => {
      // Check if already loaded
      if (window.Square) {
        setSquareLoaded(true);
        return;
      }

      const environment = getSquareEnvironment();
      const isProduction = environment === 'production';

      const script = document.createElement('script');
      script.src = isProduction
        ? 'https://web.squarecdn.com/v1/square.js'
        : 'https://sandbox.web.squarecdn.com/v1/square.js';
      script.async = true;

      script.onload = () => {
        console.log('[Square] SDK loaded successfully');
        setSquareLoaded(true);
      };

      script.onerror = () => {
        console.error('[Square] Failed to load SDK');
        setPaymentError('Failed to load payment system. Please refresh and try again.');
      };

      document.body.appendChild(script);
    };

    loadSquareSDK();
  }, [hasCustomSquareCredentials, getSquareEnvironment]);
  
  const loadLoan = useCallback(async () => {
    try {
      const response = await getLoan(id);
      setLoan(response.data);
      // The whole monthly payment, not its P&I portion. This is only the
      // opening value -- quotePayment() replaces it with the server's own total
      // moments later -- but a buyer who sees 97.50 flash into the Pay Now box
      // on a loan that owes 107.08 has been told the wrong number.
      setPaymentAmount(amountDue(response.data).toFixed(2));
      
      // Load payment history for stats
      const paymentsResponse = await fetch(`${process.env.REACT_APP_API_URL}/loans/${id}/payments`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (paymentsResponse.ok) {
        const paymentsData = await paymentsResponse.json();
        setPayments(paymentsData);
      }
      
    } catch (err) {
      setError('Failed to load loan details');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadLoan();
  }, [loadLoan]);

  /**
   * Ask the server what this payment costs and what it would do.
   *
   * The total is NEVER computed here. The server validates the submitted amount
   * against its own figure within $0.50, so a client-side total that drifted by
   * a cent would be rejected at submit time with nothing to show the customer.
   * Quoting from the same endpoint the POST validates against makes that
   * impossible.
   */
  const quotePayment = useCallback(async (extra, principalOnlyMode) => {
    setQuoting(true);
    try {
      const params = new URLSearchParams();
      if (extra) params.set('extraPrincipal', extra);
      if (principalOnlyMode) params.set('principalOnly', 'true');

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/loans/${id}/payment-breakdown?${params}`,
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );

      if (response.ok) {
        const data = await response.json();
        setPaymentBreakdown(data);
        setPaymentAmount(data.total.toFixed(2));
        setPaymentError('');
      }
    } catch (err) {
      console.error('Failed to quote payment:', err);
    } finally {
      setQuoting(false);
      setLoadingBreakdown(false);
    }
  }, [id]);

  // Re-quote as the customer types, debounced so a $5,000 entry does not fire
  // five requests on its way through $5, $50, $500.
  useEffect(() => {
    const timer = setTimeout(() => {
      quotePayment(extraPrincipal, principalOnly);
    }, 350);
    return () => clearTimeout(timer);
  }, [extraPrincipal, principalOnly, quotePayment]);

  const initializeSquarePayment = async () => {
    if (!window.Square) {
      // SDK not loaded yet - this might be a timing issue
      if (!squareLoaded) {
        setPaymentError('Payment system is still loading. Please wait a moment and try again.');
      } else {
        setPaymentError('Payment system failed to load. Please refresh the page.');
      }
      return;
    }

    try {
      // Use tenant's Square credentials
      const appId = getSquareApplicationId();
      const locationId = getSquareLocationId();

      console.log('[Square] Initializing with appId:', appId, 'locationId:', locationId);

      if (!appId || !locationId) {
        setPaymentError('Payment processing is not configured. Please contact support.');
        return;
      }

      const payments = window.Square.payments(appId, locationId);

      const card = await payments.card();
      await card.attach('#payment-card-container');
      setCardInstance(card);
      console.log('[Square] Card form initialized successfully');
    } catch (error) {
      console.error('Square initialization error:', error);
      setPaymentError(`Failed to initialize payment form: ${error.message || error}`);
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();

    // The amount comes from the server's own quote, so there is nothing to
    // re-validate here beyond the extra principal the customer typed. The
    // server checks the amount against its computed total within $0.50 and
    // enforces the payoff ceiling; duplicating either check here would just be
    // a second place to get it wrong.
    const amount = parseFloat(paymentAmount);
    const extra = parseFloat(extraPrincipal) || 0;

    if (!amount || amount <= 0) {
      setPaymentError('Payment amount is still loading. Please try again.');
      return;
    }

    if (extra < 0) {
      setPaymentError('Additional principal cannot be negative');
      return;
    }

    if (principalOnly && extra <= 0) {
      setPaymentError('Enter the amount of principal you would like to pay');
      return;
    }

    if (paymentBreakdown && extra > paymentBreakdown.maxExtraPrincipal) {
      setPaymentError(
        `That is more than this loan needs. $${formatCurrency(paymentBreakdown.maxExtraPrincipal || 0)} ` +
        'of additional principal pays it off in full.'
      );
      return;
    }

    if (!cardInstance) {
      await initializeSquarePayment();
      return;
    }

    if (submitLockRef.current) return;
    submitLockRef.current = true;

    setProcessing(true);
    setPaymentError('');

    try {
      const result = await cardInstance.tokenize();

      if (result.status === 'OK') {
        const response = await createPayment({
          loanId: loan.id,
          amount: amount,
          paymentNonce: result.token,
          paymentMethod: 'square',
          extraPrincipal: extra,
          principalOnly
        });

        // Show what the payment DID rather than navigating straight away. A
        // customer who just paid $5,000 extra has earned more than "Payment
        // Successful" -- they want the new balance and what it bought them.
        setConfirmation(response.data);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setPaymentError(result.errors?.[0]?.message || 'Payment failed');
      }
    } catch (error) {
      console.error('Payment error:', error);
      setPaymentError(error.response?.data?.error || 'Payment failed');
    } finally {
      setProcessing(false);
      submitLockRef.current = false;
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading loan details...</p>
      </div>
    );
  }

  if (error || !loan) {
    return (
      <div className="loan-detail">
        <div className="error-message">{error || 'Loan not found'}</div>
      </div>
    );
  }

  // Payment confirmation. Shown in place of the loan page so the customer sees
  // what their payment DID -- new balance, and what the extra bought them --
  // rather than a bare "success" toast on the dashboard.
  if (confirmation) {
    const fx = confirmation.effect || {};
    return (
      <div className="loan-detail">
        <div className="payment-confirmation">
          <div className="payment-confirmation-header">
            <span className="payment-confirmation-icon">
              {confirmation.paidOff ? '🎉' : '✅'}
            </span>
            <h2>
              {confirmation.paidOff
                ? 'Your loan is paid off!'
                : 'Payment received'}
            </h2>
          </div>

          <div className="payment-confirmation-figures">
            <div className="payment-confirmation-row">
              <span>Previous balance</span>
              <span>${formatCurrency(fx.previousBalance || 0)}</span>
            </div>
            <div className="payment-confirmation-row">
              <span>Principal applied</span>
              <span>−${formatCurrency(fx.principalApplied || 0)}</span>
            </div>
            {fx.extraPrincipal > 0 && (
              <div className="payment-confirmation-row highlight">
                <span>…of which extra principal</span>
                <span>${formatCurrency(fx.extraPrincipal || 0)}</span>
              </div>
            )}
            <div className="payment-confirmation-row total">
              <span>New balance</span>
              <span>${formatCurrency(confirmation.newBalance || 0)}</span>
            </div>
          </div>

          {/* Only shown when the loan actually amortizes at its current
              payment; otherwise there is no honest payoff date to promise. */}
          {!confirmation.paidOff && fx.projectionApplies && (
            <div className="payment-confirmation-effect">
              {fx.extraPrincipal > 0 && fx.paymentsSaved > 0 && (
                <p className="payment-confirmation-savings">
                  Your extra ${formatCurrency(fx.extraPrincipal || 0)} saved you{' '}
                  <strong>
                    {fx.paymentsSaved} payment{fx.paymentsSaved === 1 ? '' : 's'}
                  </strong>{' '}
                  and <strong>${formatCurrency(fx.interestSaved || 0)}</strong> in interest.
                </p>
              )}
              <p>
                {fx.paymentsRemaining} payment
                {fx.paymentsRemaining === 1 ? '' : 's'} remaining
                {fx.projectedPayoffDate && (
                  <>
                    {' '}, paid off by{' '}
                    <strong>
                      {new Date(`${fx.projectedPayoffDate}T12:00:00`).toLocaleDateString(
                        'en-US', { month: 'long', year: 'numeric' }
                      )}
                    </strong>
                  </>
                )}
              </p>
              {fx.nextPaymentDate && (
                <p className="payment-confirmation-next">
                  Next payment due{' '}
                  {new Date(`${fx.nextPaymentDate}T12:00:00`).toLocaleDateString(
                    'en-US', { month: 'long', day: 'numeric', year: 'numeric' }
                  )}
                  {fx.extraPrincipal > 0 && (
                    <small>
                      Paying extra shortens your loan. It does not skip a month.
                    </small>
                  )}
                </p>
              )}
            </div>
          )}

          <div className="payment-confirmation-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => navigate('/dashboard')}
            >
              Back to Dashboard
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setConfirmation(null);
                setExtraPrincipal('');
                setPrincipalOnly(false);
                loadLoan();
              }}
            >
              View Loan
            </button>
          </div>
        </div>
      </div>
    );
  }

  const percentPaid = ((parseFloat(loan.loan_amount) - parseFloat(loan.balance_remaining)) / parseFloat(loan.loan_amount)) * 100;
  const paymentsMade = parseInt(loan.payments_made) || payments.length;
  const remainingPayments = Math.max(0, parseInt(loan.term_months) - paymentsMade);
  
  // Calculate payment stats
  const totalPrincipalPaid = payments.reduce((sum, p) => sum + (parseFloat(p.principal_amount) || 0), 0);
  const totalInterestPaid = payments.reduce((sum, p) => sum + (parseFloat(p.interest_amount) || 0), 0);
  
  // Calculate next payment breakdown
  const monthlyInterestRate = (parseFloat(loan.interest_rate) / 100) / 12;
  const nextInterest = parseFloat(loan.balance_remaining) * monthlyInterestRate;
  const nextPrincipal = parseFloat(loan.monthly_payment) - nextInterest;
  
  // Payment status chip, from the server's own day counts and grace decision.
  // This page used to run a second copy of the day maths and bucket it at
  // 5/15/30 days, which agreed with neither the server nor the dashboard.
  const paymentStatus = overdueBadgeFor(loan);

  return (
    <div className="loan-detail">
      <div className="loan-detail-button-container">
        <button onClick={() => navigate('/dashboard')} className="btn btn-secondary">
          ← Back to Dashboard
        </button>
        <button onClick={() => navigate(`/loans/${id}/payments`)} className="btn btn-secondary">
          View Payment History
        </button>
        <button 
          onClick={() => {
            const printWindow = window.open('', '_blank', 'width=800,height=900,left=200,top=50');
            printWindow.document.write(`
              <html>
                <head>
                  <title>Loan Statement - ${loan.property_title}</title>
                  <style>
                    body { font-family: Arial, sans-serif; padding: 20px; }
                    h1 { color: #2c5f2d; }
                    table { width: 100%; border-collapse: collapse; margin: 20px 0; }
                    td { padding: 8px; border-bottom: 1px solid #ddd; }
                    .label { font-weight: bold; width: 40%; }
                    .value { text-align: right; }
                  </style>
                </head>
                <body>
                  <h1>${getCompanyLegalName()}</h1>
                  <h2>Loan Statement</h2>
                  <h3>${loan.property_title}</h3>
                  <p>${loan.location}</p>
                  <table>
                    <tr><td class="label">Purchase Price:</td><td class="value">$${formatCurrency(loan.purchase_price)}</td></tr>
                    <tr><td class="label">Down Payment:</td><td class="value">$${formatCurrency(loan.down_payment)}</td></tr>
                    <tr><td class="label">Loan Amount:</td><td class="value">$${formatCurrency(loan.loan_amount)}</td></tr>
                    <tr><td class="label">Interest Rate:</td><td class="value">${loan.interest_rate}% APR</td></tr>
                    <tr><td class="label">Term:</td><td class="value">${loan.term_months} months</td></tr>
                    <tr><td class="label">Monthly Payment:</td><td class="value">$${formatCurrency(totalMonthlyPayment(loan))}</td></tr>
                    <tr><td class="label">&nbsp;&nbsp;&nbsp;Principal &amp; Interest:</td><td class="value">$${formatCurrency(loan.monthly_payment)}</td></tr>
                    ${monthlyPaymentParts(loan).taxEscrow > 0 ? `<tr><td class="label">&nbsp;&nbsp;&nbsp;Tax Escrow:</td><td class="value">$${formatCurrency(loan.monthly_tax_escrow)}</td></tr>` : ''}
                    ${monthlyPaymentParts(loan).hoaEscrow > 0 ? `<tr><td class="label">&nbsp;&nbsp;&nbsp;HOA Escrow:</td><td class="value">$${formatCurrency(loan.monthly_hoa_escrow)}</td></tr>` : ''}
                    <tr><td class="label">Balance Remaining:</td><td class="value">$${formatCurrency(loan.balance_remaining)}</td></tr>
                    <tr><td class="label">Status:</td><td class="value">${loan.status === 'active' ? 'Active' : 'Paid Off'}</td></tr>
                    ${loan.next_payment_date ? `<tr><td class="label">Next Payment Due:</td><td class="value">${new Date(loan.next_payment_date.split('T')[0].replace(/-/g, '/')).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</td></tr>` : ''}
                  </table>
                  <p style="margin-top: 40px; font-size: 12px; color: #666;">Generated: ${new Date().toLocaleDateString()}</p>
                </body>
              </html>
            `);
            printWindow.document.close();
            printWindow.print();
          }} 
          className="btn btn-primary"
        >
          🖨️ Print Loan Statement
        </button>
      </div>

      <h1>{loan.property_title}</h1>
      <p className="loan-location">{loan.location}</p>
      
      {paymentStatus && (
        <div style={{
          backgroundColor: paymentStatus.color,
          color: 'white',
          padding: '15px 20px',
          borderRadius: '8px',
          marginTop: '20px',
          marginBottom: '20px',
          fontWeight: 'bold',
          textAlign: 'center',
          fontSize: '16px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          {paymentStatus.icon} {paymentStatus.text}
        </div>
      )}

      {/* The same notices the dashboard shows, from the same server flags and
          the same shared component, so a buyer cannot read one thing about
          their late fee on one screen and something else on the other. */}
      <LoanStatusNotices
        loan={loan}
        companyPhone={getCompanyPhone()}
        companyEmail={getCompanyEmail()}
      />

      <div className="loan-detail-grid">
        <div className="loan-info-card">
          <h2>Loan Information</h2>
          
          <div className="info-row">
            <span>Purchase Price:</span>
            <span>${formatCurrency(loan.purchase_price)}</span>
          </div>
          
          <div className="info-row">
            <span>Down Payment:</span>
            <span>${formatCurrency(loan.down_payment)}</span>
          </div>
          
          <div className="info-row">
            <span>Processing Fee:</span>
            <span>${formatCurrency(loan.processing_fee)}</span>
          </div>
          
          <div className="info-row">
            <span>Loan Amount:</span>
            <span>${formatCurrency(loan.loan_amount)}</span>
          </div>
          
          <div className="info-row">
            <span>Interest Rate:</span>
            <span>{loan.interest_rate}% APR</span>
          </div>
          
          <div className="info-row">
            <span>Term:</span>
            <span>{loan.term_months} months</span>
          </div>

          {/* THE TERM ABOVE IS WHAT WAS SIGNED. This is what is left.
              loans.term_months is stamped at origination and never revised, so
              once extra principal has been paid, or a period was short, it
              describes a schedule that no longer exists. The server derives
              this from the balance as it stands, on principal and interest
              alone, because escrow retires no principal. Nothing is written
              back: the signed term is still the signed term. */}
          {loan.projected_payoff_months !== null && loan.projected_payoff_months !== undefined && (
            <div className="info-row">
              <span>Projected payoff:</span>
              <span>{loan.projected_payoff_months} months</span>
            </div>
          )}
          
          {/* THE HEADLINE OBLIGATION. This said 97.50 -- loans.monthly_payment,
              principal and interest alone -- on a loan whose every period
              collects 107.08, with TOTAL DUE TODAY further down the same page
              showing 115.49 and nothing reconciling the three. */}
          <div className="info-row">
            <span>{arrearsBalance(loan) > 0 ? 'Amount Due:' : 'Monthly Payment:'}</span>
            <span className="highlight">${formatCurrency(amountDue(loan))}</span>
          </div>

          {/* And what part of it is catching up rather than this month. A
              buyer whose headline jumped from 107.08 to 114.16 with nothing
              explaining the difference would reasonably think they had been
              overcharged. Omitted entirely when the loan is square, which is
              every loan until one falls short. */}
          {arrearsBalance(loan) > 0 && (
            <div className="info-row" style={{ fontSize: '0.9em', color: 'var(--color-error-text)' }}>
              <span style={{ paddingLeft: '12px' }}>Includes past-due shortfall:</span>
              <span>${formatCurrency(arrearsBalance(loan))}</span>
            </div>
          )}

          {monthlyPaymentParts(loan).hasEscrow && (
            <>
              <div className="info-row" style={{ fontSize: '0.9em', color: 'var(--color-text-light)' }}>
                <span style={{ paddingLeft: '12px' }}>Principal &amp; Interest:</span>
                <span>${formatCurrency(loan.monthly_payment)}</span>
              </div>
              {monthlyPaymentParts(loan).taxEscrow > 0 && (
                <div className="info-row" style={{ fontSize: '0.9em', color: 'var(--color-text-light)' }}>
                  <span style={{ paddingLeft: '12px' }}>Tax Escrow:</span>
                  <span>${formatCurrency(loan.monthly_tax_escrow)}</span>
                </div>
              )}
              {monthlyPaymentParts(loan).hoaEscrow > 0 && (
                <div className="info-row" style={{ fontSize: '0.9em', color: 'var(--color-text-light)' }}>
                  <span style={{ paddingLeft: '12px' }}>HOA Escrow:</span>
                  <span>${formatCurrency(loan.monthly_hoa_escrow)}</span>
                </div>
              )}
            </>
          )}
          
          <div className="info-row">
            <span>Total Amount:</span>
            <span>${formatCurrency(loan.total_amount)}</span>
          </div>
          
          <div className="info-row">
            <span>Remaining Balance:</span>
            <span className="highlight">
              ${formatCurrency(loan.balance_remaining)}
            </span>
          </div>
          
          <div className="info-row">
            <span>Payments Remaining:</span>
            <span>{remainingPayments}</span>
          </div>
          
          <div className="info-row">
            <span>Status:</span>
            <span style={{ 
              display: 'inline-block',
              padding: '6px 16px', 
              borderRadius: '16px', 
              fontSize: '14px',
              fontWeight: '700',
              backgroundColor: loan.status === 'active' ? '#d4edda' : '#d1ecf1',
              color: loan.status === 'active' ? '#155724 !important' : '#0c5460 !important',
              border: loan.status === 'active' ? '1px solid #c3e6cb' : '1px solid #bee5eb'
            }}>
              {loan.status === 'active' ? 'Active' : loan.status === 'paid_off' ? 'Paid Off' : loan.status}
            </span>
          </div>
          
          {loan.next_payment_date && loan.status === 'active' && (
            <div className="info-row">
              <span>Next Payment Due:</span>
              <span className="next-payment-date">
                {new Date(loan.next_payment_date.split('T')[0].replace(/-/g, '/')).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </span>
            </div>
          )}

          <div className="progress-section">
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${Math.min(percentPaid, 100)}%` }}
              ></div>
            </div>
            <p className="progress-text">{Math.round(percentPaid)}% Paid Off</p>
          </div>

          {payments.length > 0 && (
            <>
              <hr className="info-section-separator" />
              
              <h3 className="info-section-header">💰 Payment Breakdown</h3>
              
              <div className="info-row">
                <span>Total Principal Paid:</span>
                <span className="info-value-principal">
                  ${formatCurrency(totalPrincipalPaid)}
                </span>
              </div>
              
              <div className="info-row">
                <span>Total Interest Paid:</span>
                <span className="info-value-interest">
                  ${formatCurrency(totalInterestPaid)}
                </span>
              </div>
              
              <hr className="info-section-separator" />
              
              <h3 className="info-section-header">📊 Next Payment Breakdown</h3>
              
              <div className="info-row">
                <span>Principal:</span>
                <span className="info-value-principal">
                  ${formatCurrency(nextPrincipal)}
                </span>
              </div>
              
              <div className="info-row">
                <span>Interest:</span>
                <span className="info-value-interest">
                  ${formatCurrency(nextInterest)}
                </span>
              </div>
              
              {paymentBreakdown && paymentBreakdown.monthlyTax > 0 && (
                <div className="info-row">
                  <span>Property Tax:</span>
                  <span className="info-value-tax">
                    ${formatCurrency(paymentBreakdown.monthlyTax)}
                  </span>
                </div>
              )}
              
              {paymentBreakdown && paymentBreakdown.monthlyHoa > 0 && (
                <div className="info-row">
                  <span>HOA Fee:</span>
                  <span className="info-value-hoa">
                    ${formatCurrency(paymentBreakdown.monthlyHoa)}
                  </span>
                </div>
              )}
              
              {/* What the rows above add up to. Principal and Interest are the
                  split of the P&I payment; the escrow rows are collected on top
                  of it. Without this line the section shows the components of
                  two different totals and states neither. */}
              <div className="info-row" style={{ fontWeight: '600', borderTop: '1px solid var(--color-border)', paddingTop: '8px', marginTop: '4px' }}>
                <span>Monthly Payment:</span>
                <span>${formatCurrency(totalMonthlyPayment(loan))}</span>
              </div>

              <p className="payment-tip">
                💡 Tip: Pay extra to reduce interest and own your land faster!
              </p>
            </>
          )}
        </div>

        {loan.status === 'active' && parseFloat(loan.balance_remaining) > 0 && (
          <div className="payment-card">
            <h2>Make a Payment</h2>

            {/* Check if payment processing is configured */}
            {!hasCustomSquareCredentials() ? (
              <div style={{
                padding: '20px',
                background: 'var(--color-warning-bg)',
                border: '1px solid var(--color-warning-border)',
                borderRadius: '8px',
                marginTop: '1rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                  <span style={{ fontSize: '1.5rem' }}>📞</span>
                  <strong style={{ color: 'var(--color-warning-text)' }}>Online payments are not currently available</strong>
                </div>
                <p style={{ margin: 0, color: 'var(--color-warning-text)' }}>
                  Please contact us directly for payment options:
                  <br />
                  <strong>Phone:</strong> {getCompanyPhone()}
                  <br />
                  <strong>Email:</strong> {getCompanyEmail()}
                </p>
              </div>
            ) : (
              <>
                <div className="pay-extra-container">
                  <h3 className="pay-extra-title">💡 Pay Extra & Save!</h3>
                  <PayExtraCalculator loan={loan} />
                </div>

                <form onSubmit={handlePayment}>
              {paymentBreakdown && !loadingBreakdown && (
                <div className="payment-breakdown-box">
                  <h3 className="payment-breakdown-title">💳 Payment Breakdown</h3>
                  
                  <div className="payment-breakdown-items">
                    {/* THE OBLIGATION, LINE BY LINE, THEN THE FEES ON TOP.
                        The buyer has to be able to read the 107.08 they owe and
                        the 8.41 the card costs as two separate numbers. Before
                        this the only total on the screen was 115.49 with the two
                        fused, and the escrow rows were labelled "Estimated" --
                        a term frozen on the loan and printed in the contract is
                        not an estimate. */}
                    {paymentBreakdown.loanPayment > 0 && (
                      <div className="payment-breakdown-row">
                        <span>Principal &amp; Interest:</span>
                        <span className="payment-breakdown-row-value">${paymentBreakdown.loanPayment.toFixed(2)}</span>
                      </div>
                    )}

                    {paymentBreakdown.monthlyTax > 0 && (
                      <div className="payment-breakdown-row">
                        <span>Tax Escrow:</span>
                        <span className="payment-breakdown-row-value">${paymentBreakdown.monthlyTax.toFixed(2)}</span>
                      </div>
                    )}

                    {paymentBreakdown.monthlyHoa > 0 && (
                      <div className="payment-breakdown-row">
                        <span>HOA Escrow:</span>
                        <span className="payment-breakdown-row-value">${paymentBreakdown.monthlyHoa.toFixed(2)}</span>
                      </div>
                    )}

                    {/* The scheduled payment in full, before any fee or arrears.
                        Rendered only when escrow is actually collected: with
                        none, this would restate the P&I row directly above it. */}
                    {(paymentBreakdown.monthlyTax > 0 || paymentBreakdown.monthlyHoa > 0) && (
                      <div className="payment-breakdown-row" style={{ fontWeight: '600' }}>
                        <span>Monthly Payment:</span>
                        <span className="payment-breakdown-row-value">
                          ${(paymentBreakdown.loanPayment
                            + paymentBreakdown.monthlyTax
                            + paymentBreakdown.monthlyHoa).toFixed(2)}
                        </span>
                      </div>
                    )}

                    {/* What is being caught up, between the scheduled payment
                        and the card's own charges. It sits here because that is
                        where it falls in the total: the buyer owes it, the card
                        fees are charged on it, and a line further down would
                        read as though the processor levied it. */}
                    {paymentBreakdown.arrears > 0 && (
                      <div className="payment-breakdown-row" style={{ color: 'var(--color-error-text)' }}>
                        <span>Arrears (past-due shortfall):</span>
                        <span className="payment-breakdown-row-value">
                          ${paymentBreakdown.arrears.toFixed(2)}
                        </span>
                      </div>
                    )}
                    
                    {paymentBreakdown.lateFee > 0 && (
                      <div className="payment-breakdown-row late-fee">
                        <span>Late Fee ({paymentBreakdown.daysOverdue} {paymentBreakdown.daysOverdue === 1 ? 'day' : 'days'} overdue):</span>
                        <span className="payment-breakdown-row-value">${paymentBreakdown.lateFee.toFixed(2)}</span>
                      </div>
                    )}
                    
                    {paymentBreakdown.noticeFee > 0 && (
                      <div className="payment-breakdown-row notice-fee">
                        <span>Default/Cure Notice Fee:</span>
                        <span className="payment-breakdown-row-value">${paymentBreakdown.noticeFee.toFixed(2)}</span>
                      </div>
                    )}
                    
                    {paymentBreakdown.postalFee > 0 && (
                      <div className="payment-breakdown-row postal-fee">
                        <span>Postal/Certified Mail Fee:</span>
                        <span className="payment-breakdown-row-value">${paymentBreakdown.postalFee.toFixed(2)}</span>
                      </div>
                    )}

                    {paymentBreakdown.extraPrincipal > 0 && (
                      <div className="payment-breakdown-row extra-principal">
                        <span>Additional Principal:</span>
                        <span className="payment-breakdown-row-value">
                          ${paymentBreakdown.extraPrincipal.toFixed(2)}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="payment-breakdown-subtotal">
                    <div className="payment-breakdown-subtotal-row label">
                      <span>Subtotal:</span>
                      <span>${paymentBreakdown.subtotal.toFixed(2)}</span>
                    </div>
                    
                    <div className="payment-breakdown-fee-row">
                      <span>Square Processing Fee:</span>
                      <span>${paymentBreakdown.squareFee.toFixed(2)}</span>
                    </div>
                    
                    <div className="payment-breakdown-fee-row">
                      <span>Credit Card Processing Fee:</span>
                      <span>${paymentBreakdown.convenienceFee.toFixed(2)}</span>
                    </div>
                  </div>
                  
                  <div className="payment-breakdown-total">
                    <div className="payment-breakdown-total-row">
                      <span className="payment-breakdown-total-label">TOTAL DUE TODAY:</span>
                      <span className="payment-breakdown-total-amount">
                        ${paymentBreakdown.total.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="payment-amount-section">
                {/* Principal-only mode is offered ONLY when nothing is due.
                    When a payment IS due the extra rides along with it, so the
                    customer can never pay ahead into a still-delinquent loan. */}
                {paymentBreakdown && !paymentBreakdown.paymentIsDue && (
                  <div className="principal-only-toggle">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={principalOnly}
                        onChange={(e) => {
                          setPrincipalOnly(e.target.checked);
                          setSelectedQuickAmount(null);
                        }}
                      />
                      <span>
                        Make a principal-only payment
                        <small>
                          Nothing is due right now. Pay straight down the balance:
                          no interest, no escrow, no fees.
                        </small>
                      </span>
                    </label>
                  </div>
                )}

                <label htmlFor="extra-principal">
                  {principalOnly ? 'Principal Payment:' : 'Additional Principal (optional):'}
                </label>
                <div className="amount-input-group">
                  <span className="currency-symbol">$</span>
                  <input
                    id="extra-principal"
                    type="number"
                    inputMode="decimal"
                    value={extraPrincipal}
                    onChange={(e) => {
                      setExtraPrincipal(e.target.value);
                      setSelectedQuickAmount(null);
                    }}
                    min="0"
                    max={paymentBreakdown ? paymentBreakdown.maxExtraPrincipal : undefined}
                    step="0.01"
                    placeholder="0.00"
                    required={principalOnly}
                  />
                </div>

                <div className="quick-amount-buttons">
                  <button
                    type="button"
                    className={`btn btn-small ${selectedQuickAmount === 'monthly' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => {
                      setExtraPrincipal('');
                      setPrincipalOnly(false);
                      setSelectedQuickAmount('monthly');
                    }}
                  >
                    {principalOnly ? 'Scheduled Payment' : 'No Extra'}
                  </button>
                  <button
                    type="button"
                    className={`btn btn-small ${selectedQuickAmount === 'payoff' ? 'btn-primary' : 'btn-secondary'}`}
                    disabled={!paymentBreakdown}
                    onClick={() => {
                      // The payoff figure comes from the server, so it is exact.
                      // Typing the balance by hand would be rejected: payoff also
                      // includes this period's interest and any fees.
                      setExtraPrincipal(paymentBreakdown.maxExtraPrincipal.toFixed(2));
                      setSelectedQuickAmount('payoff');
                    }}
                  >
                    Pay Off Loan
                  </button>
                </div>

                <div className="payment-total-line">
                  <span>Total charged today:</span>
                  <strong>
                    {quoting ? 'Calculating…' : `$${formatCurrency(paymentAmount || 0)}`}
                  </strong>
                </div>

                <p className="payment-note">
                  Current balance: ${formatCurrency(loan.balance_remaining)}
                  {paymentBreakdown && (
                    <> | ${formatCurrency(paymentBreakdown.maxExtraPrincipal || 0)} of extra principal pays this loan off</>
                  )}
                </p>

                {/* What the extra principal buys, quoted before they commit. */}
                {paymentBreakdown?.effect?.projectionApplies
                  && paymentBreakdown.effect.paymentsSaved > 0 && (
                  <div className="extra-principal-savings">
                    <strong>This payment saves you</strong>
                    <span>
                      {paymentBreakdown.effect.paymentsSaved} payment
                      {paymentBreakdown.effect.paymentsSaved === 1 ? '' : 's'} and
                      ${formatCurrency(paymentBreakdown.effect.interestSaved || 0)} in interest
                    </span>
                    {paymentBreakdown.effect.projectedPayoffDate && (
                      <small>
                        New payoff date: {new Date(
                          `${paymentBreakdown.effect.projectedPayoffDate}T12:00:00`
                        ).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                      </small>
                    )}
                  </div>
                )}
              </div>

              <div className="billing-info-section">
                <h3 className="billing-info-title">Billing Information</h3>
                
                <div className="form-group">
                  <label>Cardholder Name *</label>
                  <input
                    type="text"
                    value={billingName}
                    onChange={(e) => setBillingName(e.target.value)}
                    placeholder="John Doe"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Billing Address *</label>
                  <input
                    type="text"
                    value={billingAddress}
                    onChange={(e) => setBillingAddress(e.target.value)}
                    placeholder="123 Main St"
                    required
                  />
                </div>

                <div className="billing-info-grid">
                  <div className="form-group">
                    <label>City *</label>
                    <input
                      type="text"
                      value={billingCity}
                      onChange={(e) => setBillingCity(e.target.value)}
                      placeholder="Appleton"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>State *</label>
                    <select
                      value={billingState}
                      onChange={(e) => setBillingState(e.target.value)}
                      required
                    >
                      <option value="">Select</option>
                      <option value="WI">WI</option>
                      <option value="IL">IL</option>
                      <option value="MI">MI</option>
                      <option value="MN">MN</option>
                      <option value="IA">IA</option>
                      <option value="AZ">AZ</option>
                      <option value="AR">AR</option>
                      <option value="CO">CO</option>
                      <option value="AL">AL</option>
                      <option value="AK">AK</option>
                      <option value="CA">CA</option>
                      <option value="CT">CT</option>
                      <option value="DE">DE</option>
                      <option value="FL">FL</option>
                      <option value="GA">GA</option>
                      <option value="HI">HI</option>
                      <option value="ID">ID</option>
                      <option value="IN">IN</option>
                      <option value="KS">KS</option>
                      <option value="KY">KY</option>
                      <option value="LA">LA</option>
                      <option value="ME">ME</option>
                      <option value="MD">MD</option>
                      <option value="MA">MA</option>
                      <option value="MS">MS</option>
                      <option value="MO">MO</option>
                      <option value="MT">MT</option>
                      <option value="NE">NE</option>
                      <option value="NV">NV</option>
                      <option value="NH">NH</option>
                      <option value="NJ">NJ</option>
                      <option value="NM">NM</option>
                      <option value="NY">NY</option>
                      <option value="NC">NC</option>
                      <option value="ND">ND</option>
                      <option value="OH">OH</option>
                      <option value="OK">OK</option>
                      <option value="OR">OR</option>
                      <option value="PA">PA</option>
                      <option value="RI">RI</option>
                      <option value="SC">SC</option>
                      <option value="SD">SD</option>
                      <option value="TN">TN</option>
                      <option value="TX">TX</option>
                      <option value="UT">UT</option>
                      <option value="VT">VT</option>
                      <option value="VA">VA</option>
                      <option value="WA">WA</option>
                      <option value="WV">WV</option>
                      <option value="WY">WY</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>ZIP *</label>
                    <input
                      type="text"
                      value={billingZip}
                      onChange={(e) => setBillingZip(e.target.value)}
                      placeholder="54911"
                      maxLength="5"
                      pattern="[0-9]{5}"
                      required
                    />
                  </div>
                </div>
              </div>

              <div id="payment-card-container"></div>

              {paymentError && (
                <div className="error-message">
                  {paymentError}
                </div>
              )}

              {!cardInstance ? (
                <button
                  type="button"
                  className="btn btn-primary btn-full-width"
                  onClick={initializeSquarePayment}
                  disabled={processing}
                >
                  Continue to Payment
                </button>
              ) : (
                <button
                  type="submit"
                  className="btn btn-primary btn-full-width"
                  disabled={processing}
                >
                  {processing ? 'Processing...' : `Pay $${formatCurrency(paymentAmount || 0)}`}
                </button>
              )}
            </form>
              </>
            )}
          </div>
        )}
      </div>

      {loan.status === 'paid_off' && (
        <div className="success-message">
          🎉 Congratulations! This loan has been paid off!
        </div>
      )}
    </div>
  );
}

// Pay Extra Calculator Component
//
// ---------------------------------------------------------------------------
// WHAT THIS USED TO DO, AND WHY IT WAS WRONG TWICE OVER
// ---------------------------------------------------------------------------
// Math.ceil(balance / monthly) -- the arithmetic of an interest-free loan. On
// the reference loan it promised a payoff 27 months out against a true 30 at
// 7.99%, and every "months earlier" and "interest saved" figure beneath it was
// derived from that same false schedule, so all three numbers on the screen
// were wrong together and agreed with each other. It also divided by
// loans.monthly_payment while calling the result a monthly payment.
//
// The projection now comes from the server's projectPayoff(), which is the
// amortization the capture routes and the seed already use and rounds to cents
// each period. Nothing here re-derives it.
//
// ---------------------------------------------------------------------------
// ESCROW IS EXCLUDED FROM THE PAYOFF, AND SHOWN IN THE PAYMENT
// ---------------------------------------------------------------------------
// Tax and HOA escrow are collected with the payment but pay the county and the
// association. They retire no principal -- Section 8(b) of the contract now
// says so outright -- so an extra dollar here is an extra dollar of PRINCIPAL,
// and the payoff maths runs on P&I alone. The "New Monthly Payment" line is the
// one place the escrow belongs, because that is what leaves the buyer's
// account, and it names the P&I portion so the two are never confused.
function PayExtraCalculator({ loan }) {
  const [extraPayment, setExtraPayment] = useState('');
  const [projection, setProjection] = useState(null);

  const extra = parseFloat(extraPayment) || 0;

  // Debounced exactly like the payment quote above it: a $500 entry must not
  // fire four requests on its way through 5, 50 and 500.
  useEffect(() => {
    const timer = setTimeout(async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/loans/${loan.id}/payoff-projection?extraMonthly=${extra}`,
          { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
        );
        if (response.ok) {
          setProjection(await response.json());
        }
      } catch (err) {
        console.error('Failed to project payoff:', err);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [extra, loan.id]);

  const parts = monthlyPaymentParts(loan);
  // What actually leaves their account: the whole monthly payment plus the
  // extra principal they are considering.
  const newMonthlyTotal = parts.total + extra;

  // Months are added as MONTHS, not as 30-day blocks. The old version drifted
  // by five days a year and told a buyer on a 120-month loan the wrong month.
  const payoffDate = (months) => {
    const date = new Date();
    date.setMonth(date.getMonth() + months);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  return (
    <div>
      <div className="form-group">
        <label>Extra Payment Amount (per month):</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '20px', fontWeight: 'bold' }}>$</span>
          <input
            type="number"
            value={extraPayment}
            onChange={(e) => setExtraPayment(e.target.value)}
            placeholder="50"
            min="0"
            step="10"
            style={{ flex: 1 }}
          />
        </div>
        <small style={{ color: 'var(--color-text-light)' }}>
          Goes straight to principal. Escrow is passed on to the county and any
          association, so it never reduces your balance.
        </small>
      </div>

      {extra > 0 && projection && projection.applies && (
        <div className="pay-extra-results">
          <h3 className="pay-extra-results-title">💰 Your Savings:</h3>

          <div className="pay-extra-monthly">
            <strong>New Monthly Payment:</strong> ${formatCurrency(newMonthlyTotal)}
            {parts.hasEscrow && (
              <small style={{ display: 'block', color: 'var(--color-text-light)' }}>
                Principal &amp; interest ${formatCurrency(parts.principalAndInterest + extra)}
                {parts.taxEscrow > 0 && <> + tax escrow ${formatCurrency(parts.taxEscrow)}</>}
                {parts.hoaEscrow > 0 && <> + HOA escrow ${formatCurrency(parts.hoaEscrow)}</>}
              </small>
            )}
          </div>

          <div className="pay-extra-savings">
            ⏱️ Pay off {projection.monthsSaved} month
            {projection.monthsSaved === 1 ? '' : 's'} earlier!
          </div>

          <div className="pay-extra-savings">
            💵 Save ${formatCurrency(projection.interestSaved)} in interest!
          </div>

          <div className="pay-extra-payoff-date">
            <strong>Own your land by:</strong> {payoffDate(projection.withExtra.months)}
            <small style={{ display: 'block', color: 'var(--color-text-light)' }}>
              {projection.baseline.months} months at your current payment,
              {' '}{projection.withExtra.months} with the extra.
            </small>
          </div>
        </div>
      )}
    </div>
  );
}

export default LoanDetail;